import React, { useState } from "react";
import styles from "./ChatMessage.module.scss";

/**
 * 카카오톡 스타일 다중 사진 그리드 (1~4 전용, 5장 이상은 2×2 + 마지막 칸 +N)
 * @param {(url: string, allUrls: string[], allNames: (string|null)[]) => void} [onImageClick]
 */
function MessageImageGrid({ urls, thumbnailUrls, fileNames, onImageClick, fallbackSrc }) {
  const n = urls.length;
  const [failedSet, setFailedSet] = useState(() => new Set());
  if (n === 0) return null;

  // thumbnailUrls가 없으면 원본 urls를 그대로 사용 (기존 이미지 호환)
  const thumbs = Array.isArray(thumbnailUrls) && thumbnailUrls.length === n ? thumbnailUrls : urls;

  const namesAligned = urls.map((_, i) =>
    Array.isArray(fileNames) && fileNames[i] != null && fileNames[i] !== "" ? fileNames[i] : null
  );

  const resolveThumb = (thumb) => (failedSet.has(thumb) && fallbackSrc ? fallbackSrc : thumb);

  const renderImg = (thumb) => (
    <img
      src={thumb}
      alt=""
      className={styles.photoImg}
      onError={(e) => {
        if (fallbackSrc) {
          e.currentTarget.src = fallbackSrc;
          setFailedSet((prev) => new Set([...prev, thumb]));
        }
      }}
    />
  );

  const wrapClickable = (reactKey, idx, cellClass, children, extra) => {
    const cls = `${styles.photoCell} ${cellClass}`.trim();
    if (onImageClick) {
      return (
        <button
          type="button"
          key={reactKey}
          className={cls}
          onClick={() => onImageClick(thumbs[idx], thumbs, namesAligned, urls)}
        >
          {children}
          {extra}
        </button>
      );
    }
    return (
      <div key={reactKey} className={cls}>
        {children}
        {extra}
      </div>
    );
  };

  if (n === 1) {
    return (
      <div className={`${styles.photoGrid} ${styles.photoGrid1}`}>
        {wrapClickable("0", 0, styles.photoGrid1Cell, renderImg(thumbs[0]))}
      </div>
    );
  }

  if (n === 2) {
    return (
      <div className={`${styles.photoGrid} ${styles.photoGrid2}`}>
        {thumbs.slice(0, 2).map((thumb, i) =>
          wrapClickable(String(i), i, styles.photoGridEqCell, renderImg(thumb))
        )}
      </div>
    );
  }

  if (n === 3) {
    return (
      <div className={`${styles.photoGrid} ${styles.photoGrid3}`}>
        {wrapClickable("0", 0, styles.photoGrid3Main, renderImg(thumbs[0]))}
        {wrapClickable("1", 1, styles.photoGrid3SideTop, renderImg(thumbs[1]))}
        {wrapClickable("2", 2, styles.photoGrid3SideBot, renderImg(thumbs[2]))}
      </div>
    );
  }

  if (n === 4) {
    return (
      <div className={`${styles.photoGrid} ${styles.photoGrid4}`}>
        {thumbs.slice(0, 4).map((thumb, i) =>
          wrapClickable(String(i), i, styles.photoGridEqCell, renderImg(thumb))
        )}
      </div>
    );
  }

  const more = n - 4;
  return (
    <div className={`${styles.photoGrid} ${styles.photoGridMany}`}>
      {wrapClickable("0", 0, styles.photoGridMany0, renderImg(thumbs[0]))}
      {wrapClickable("1", 1, styles.photoGridMany1, renderImg(thumbs[1]))}
      {wrapClickable("2", 2, styles.photoGridMany2, renderImg(thumbs[2]))}
      {wrapClickable(
        "3",
        3,
        styles.photoGridMany3,
        renderImg(thumbs[3]),
        more > 0 ? <span className={styles.photoMoreOverlay}>+{more}</span> : null
      )}
    </div>
  );
}

export default MessageImageGrid;
